import Box from '@mui/material/Box';
import { SxProps } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { mdiChevronRight, mdiLightbulbOutline } from '@mdi/js';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { Chip } from 'common/components/Chip';
import { CircularProgress } from 'common/components/CircularProgress';
import { Icon } from 'common/components/Icon';
import {
    ListItem,
    ListItemActionText,
    ListItemMedia,
    ListItemSecondaryAction,
    ListItemText,
} from 'common/components/ListItem';
import { formatDate } from 'common/utils/formatDate';
import { receivedCertificateEndDateNear } from 'common/utils/receivedCertificate';
import { IBasicFileFragment, ICompliancy, Maybe } from 'graphql/types';

const statusIconWrapperStyle: SxProps = {
    position: 'absolute',
    top: 0,
    left: 0,
    pr: '4px',
    pb: '4px',
    backgroundColor: 'common.white',
    borderTopLeftRadius: '4px',
    borderBottomRightRadius: 10,
};

const statusIconInnerStyle: SxProps = {
    display: 'flex',
    borderTopLeftRadius: '4px',
    borderBottomRightRadius: 7,
};

interface ISkill {
    id: string;
    name: string;
    thumbnail?: Maybe<IBasicFileFragment>;
}

interface ISkillRequirementStatus {
    minAbsolute?: number | null;
    minPercent?: number | null;
    hasMandatoryCertificates?: boolean;
    compliancy?: Maybe<
        Pick<
            ICompliancy,
            | 'isCompliant'
            | 'count'
            | 'certificateProgress'
            | 'nearestExpiryDate'
        >
    >;
}

interface IProps {
    skill: ISkill;
    skillRequirement?: ISkillRequirementStatus;
    groupUserCount?: Maybe<number>;
    onClick?(): void;
}

export const GroupSkillPortfolioListItem = ({
    skill,
    skillRequirement,
    groupUserCount,
    onClick,
}: IProps) => {
    const [translate] = useTranslation();
    const { name, thumbnail } = skill;

    const image = thumbnail?.url || undefined;

    const hasMinThreshold =
        !!skillRequirement?.minAbsolute || !!skillRequirement?.minPercent;

    const isCompliant = skillRequirement?.compliancy?.isCompliant === true;

    const nearestExpiryDate = skillRequirement?.compliancy?.nearestExpiryDate;
    const isExpiringSoon =
        !!nearestExpiryDate &&
        receivedCertificateEndDateNear(nearestExpiryDate);

    const compliancyProgress = useMemo(() => {
        const compliancy = skillRequirement?.compliancy;
        if (hasMinThreshold || !compliancy) return null;

        const { certificateProgress } = compliancy;
        if (certificateProgress == null) return null;

        return Math.round(certificateProgress * 100);
    }, [skillRequirement, hasMinThreshold]);

    const isProgressComplete = !hasMinThreshold && compliancyProgress === 100;
    const isAchieved = hasMinThreshold ? isCompliant : isProgressComplete;

    const compliancySecondary = useMemo(() => {
        const compliancy = skillRequirement?.compliancy;
        if (!compliancy) return undefined;

        const { minAbsolute, minPercent, hasMandatoryCertificates } =
            skillRequirement;
        const compliantCount = compliancy.count ?? 0;

        let baseText: string | undefined;

        if (
            hasMinThreshold &&
            hasMandatoryCertificates &&
            compliantCount === 0
        ) {
            baseText = translate('groupSkills.compliancyNoneStarted');
        } else if (minAbsolute && minPercent) {
            const absoluteMet = compliantCount >= minAbsolute;
            const percentMet =
                groupUserCount != null && groupUserCount > 0
                    ? (compliantCount / groupUserCount) * 100 >= minPercent
                    : false;

            if (absoluteMet && percentMet) {
                baseText = translate('groupSkills.compliancyConditionsMet');
            } else if (!absoluteMet && !percentMet) {
                baseText = translate('groupSkills.compliancyConditionsNotMet');
            } else if (!absoluteMet) {
                baseText = hasMandatoryCertificates
                    ? translate('groupSkills.compliancyBelow', {
                          participantAmount: minAbsolute,
                      })
                    : translate('groupSkills.compliancyProgressCount', {
                          count: compliantCount,
                          participantAmount: minAbsolute,
                      });
            } else {
                baseText = translate(
                    hasMandatoryCertificates
                        ? 'groupSkills.compliancyPercentBelow'
                        : 'groupSkills.compliancyProgressPercentBelow',
                    { minPercent }
                );
            }
        } else if (minAbsolute) {
            const participantAmount = minAbsolute;

            if (hasMandatoryCertificates) {
                if (compliantCount < participantAmount) {
                    baseText = translate('groupSkills.compliancyBelow', {
                        participantAmount,
                    });
                } else if (compliantCount > participantAmount) {
                    baseText = translate('groupSkills.compliancyAbove', {
                        participantAmount,
                    });
                } else {
                    baseText = translate('groupSkills.compliancyEqual', {
                        count: compliantCount,
                        participantAmount,
                    });
                }
            } else {
                baseText = translate('groupSkills.compliancyProgressCount', {
                    count: compliantCount,
                    participantAmount,
                });
            }
        } else if (minPercent && groupUserCount != null && groupUserCount > 0) {
            const percent = Math.round((compliantCount / groupUserCount) * 100);

            if (hasMandatoryCertificates) {
                if (percent < minPercent) {
                    baseText = translate('groupSkills.compliancyPercentBelow', {
                        minPercent,
                    });
                } else if (percent > minPercent) {
                    baseText = translate('groupSkills.compliancyPercentAbove', {
                        minPercent,
                    });
                } else {
                    baseText = translate('groupSkills.compliancyPercentEqual', {
                        percent,
                    });
                }
            } else if (percent < minPercent) {
                baseText = translate(
                    'groupSkills.compliancyProgressPercentBelow',
                    { minPercent }
                );
            } else if (percent > minPercent) {
                baseText = translate(
                    'groupSkills.compliancyProgressPercentAbove',
                    { minPercent }
                );
            } else {
                baseText = translate(
                    'groupSkills.compliancyProgressPercentEqual',
                    { percent }
                );
            }
        }

        const expiryText =
            isExpiringSoon && nearestExpiryDate && isAchieved
                ? translate('receivedCertificateExpiryNear', {
                      date: formatDate(nearestExpiryDate, 'dd MMMM yyyy'),
                  })
                : undefined;

        if (baseText && expiryText) {
            return (
                <>
                    {baseText}
                    <br />
                    {expiryText}
                </>
            );
        }

        return baseText ?? expiryText;
    }, [
        skillRequirement,
        groupUserCount,
        hasMinThreshold,
        isAchieved,
        isExpiringSoon,
        nearestExpiryDate,
        translate,
    ]);

    const showProgressIndicator =
        !!skillRequirement?.compliancy &&
        !hasMinThreshold &&
        !isProgressComplete;

    const showStatusChip =
        !!skillRequirement?.compliancy &&
        (hasMinThreshold || isProgressComplete);

    const isStatusError = hasMinThreshold && !isCompliant;
    const isStatusWarning = !isStatusError && isExpiringSoon && isAchieved;

    return (
        <ListItem button px={0} py={1.5} onClick={onClick}>
            <Box sx={{ position: 'relative' }}>
                <ListItemMedia color="primary" image={image}>
                    <Icon
                        className="icon"
                        path={mdiLightbulbOutline}
                        size="2.2rem"
                        sizeSm="3rem"
                    />
                </ListItemMedia>

                {showProgressIndicator && (
                    <Box sx={statusIconWrapperStyle}>
                        <CircularProgress
                            filled
                            showTooltip
                            bgColor="grey.200"
                            color="inherit"
                            size={28}
                            value={compliancyProgress ?? 0}
                            variant="determinate"
                        />
                    </Box>
                )}

                {showStatusChip && (
                    <Box sx={statusIconWrapperStyle}>
                        <Box
                            sx={[
                                statusIconInnerStyle,
                                {
                                    backgroundColor: isStatusError
                                        ? 'error.main'
                                        : isStatusWarning
                                          ? 'warning.main'
                                          : 'success.main',
                                },
                            ]}
                        >
                            <Chip
                                color={
                                    isStatusError
                                        ? 'error'
                                        : isStatusWarning
                                          ? 'warning'
                                          : 'success'
                                }
                                icon={
                                    isStatusError ? (
                                        <CloseIcon />
                                    ) : (
                                        <CheckIcon />
                                    )
                                }
                                sx={{
                                    '.MuiChip-icon': {
                                        mx: 'auto',
                                        color: 'common.white',
                                        fontSize: 16,
                                        strokeWidth: 2,
                                        stroke: 'currentColor',
                                    },
                                    '.MuiChip-label': { display: 'none' },
                                }}
                            />
                        </Box>
                    </Box>
                )}
            </Box>

            <ListItemText primary={name} secondary={compliancySecondary} />

            <ListItemActionText>{translate('open')}</ListItemActionText>

            <ListItemSecondaryAction hideXs>
                <Icon path={mdiChevronRight} size="4rem" />
            </ListItemSecondaryAction>
        </ListItem>
    );
};
